import { X } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { AccentButton, NeutralButton } from '../../../../../../shared/Buttons';
import { Entity } from '../../../../../../types/apiTypes';
import useApi from '../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../utils/UserContext';
import { Select, Input } from 'antd';
import { Truck } from '../../../../../../types/dbTypes';
import { useForm } from 'react-hook-form';

// Define the form data type
interface InviteDriverFormData {
  firstName: string;
  lastName: string;
  email: string;
  truckId: string;
}

export const InviteDriverModal = ({
  isOpen,
  onClose,
  trucks,
  trucksLoading,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  trucks: Truck[];
  trucksLoading: boolean;
  onSuccess: () => void;
}) => {
  const { userRecord, userId } = useUser();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [loading, setLoading] = useState(false);

  // Add this inside your component
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<InviteDriverFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      truckId: '',
    },
  });

  const selectedTruck = watch('truckId');
  // Watch all form fields to check if form is complete
  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const email = watch('email');

  // Check if all required fields have values - use explicit boolean checks
  const isFormComplete =
    Boolean(firstName) &&
    Boolean(lastName) &&
    Boolean(email) &&
    Boolean(selectedTruck);

  // Modified truck selection handler
  const handleTruckChange = (value: string) => {
    setValue('truckId', value);
    // If you had other logic in handleChange, keep it here
  };

  // Form submission handler
  const onSubmit = (data: InviteDriverFormData) => {
    // Handle form submission logic here
    sendInvite({
      customer_id: userId ?? '',
      truck_id: data.truckId,
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
    });
  };

  const sendInvite = async ({
    customer_id,
    truck_id,
    email,
    first_name,
    last_name,
  }: {
    customer_id: string;
    truck_id: string;
    email: string;
    first_name: string;
    last_name: string;
  }) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        `/driverInvites`,
        'post',
        {
          truck_id: truck_id,
          customer_id: customer_id,
          email: email,
          first_name: first_name,
          last_name: last_name,
        },
        'Error occurred while sending driver invite.'
      );
      if (response?.status === HttpStatusCode.Created) {
        toast.success('Successfully sent driver invite.');
        onSuccess();
      }
    } catch (e: any) {
      console.error('Error occurred while sending driver invite: ', e);
    } finally {
      onClose();
      setLoading(false);
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="fixed inset-0 z-[999] flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="h-auto max-h-[90%] w-full overflow-x-hidden rounded-[4px] bg-white sm:max-w-[90%] md:max-w-[60%] lg:max-w-[600px]">
              <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Invite Driver
                </h2>
                <X
                  className="hover:cursor-pointer hover:opacity-80"
                  onClick={onClose}
                  weight="regular"
                  size={24}
                />
              </div>
              {!trucksLoading && userRecord?.userType === Entity.Customer ? (
                <form
                  className="flex h-full w-full flex-col items-center gap-6 overflow-y-auto overflow-x-hidden border-[1px] border-bordergray p-6"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="flex w-full flex-row items-center space-x-4 align-middle">
                    <p>
                      Invite a new driver to your fleet and assign them to a
                      truck right away.
                    </p>
                  </div>

                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <Input
                      placeholder="Enter first name"
                      className="h-[48px] w-full"
                      {...register('firstName', {
                        required: 'First name is required',
                      })}
                      onChange={(e) => setValue('firstName', e.target.value)}
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>

                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <Input
                      placeholder="Enter last name"
                      className="h-[48px] w-full"
                      {...register('lastName', {
                        required: 'Last name is required',
                      })}
                      onChange={(e) => setValue('lastName', e.target.value)}
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>

                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      className="h-[48px] w-full"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                      onChange={(e) => setValue('email', e.target.value)}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700">
                      Select Truck for Driver
                    </label>
                    <Select
                      onChange={handleTruckChange}
                      placeholder="Select a truck"
                      options={trucks?.map((truck) => ({
                        label:
                          truck?.truck_display_name +
                          ' ' +
                          truck?.vehicle_id_number,
                        value: truck?.truck_id,
                      }))}
                      className="h-[48px] w-full min-w-full"
                      value={watch('truckId')}
                    />
                    {errors.truckId && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.truckId.message}
                      </p>
                    )}
                  </div>
                </form>
              ) : (
                <div className="flex h-full w-full items-center justify-center align-middle">
                  <ClipLoader
                    color="#1D213B"
                    loading={trucksLoading}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                </div>
              )}
              {/* Buttons outside the form */}
              <div className="flex justify-between space-x-2 p-4">
                <NeutralButton
                  onClick={onClose}
                  addClass="rounded-full w-[238px] h-[48px] flex flex-row items-center justify-center"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  submit={true}
                  onClick={handleSubmit(onSubmit)}
                  disabled={!isFormComplete}
                  addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px] disabled:opacity-50 disabled:bg-yellow-600 flex flex-row items-center justify-center"
                >
                  {!loading ? (
                    'Send Invite'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={loading}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return <>{renderScreenView(screenView)}</>;
};

export default InviteDriverModal;
